import { useEffect } from "react";
import type { TabItem } from "../kit";
import { Tabs } from "../kit";
import { useCaps } from "../editor/CapsProvider";
import { useRing } from "./ringContext";

export type TabId = "profile" | "size" | "material" | "gems" | "joints" | "engrave" | "engagement";

/** The wedding band is the base product — these six are always present. */
export const RING_TABS: (TabItem & { id: TabId })[] = [
  { id: "profile", label: "Profile", icon: "radio_button_checked" },
  { id: "size", label: "Size", icon: "straighten" },
  { id: "material", label: "Material", icon: "palette" },
  { id: "gems", label: "Gems", icon: "diamond" },
  { id: "joints", label: "Joints", icon: "join" },
  { id: "engrave", label: "Engrave", icon: "edit_note" },
];

/** Appended only while the Engagement toggle is on and the viewer speaks v37. */
export const ENGAGEMENT_TAB: TabItem & { id: TabId } = {
  id: "engagement",
  label: "Engagement",
  icon: "auto_awesome",
};

/**
 * The visible tab set.
 *
 * `crossover` drops the Gems tab: a cross-over shank carries no stones (product
 * decision 2026-09-09 — the engine gates the pave in
 * `BandConfigurator.rebuildDiamonds`), so every control on that tab would write
 * state that cannot render. Hiding the tab is the honest signal; leaving it live
 * would let a customer set a stone count, a shape and a price on a band that
 * shows none of it. The Gems tab also owns the inner diamond and the free-stone
 * editor, neither of which the crossover path places either, so the whole tab
 * goes rather than a subset of its rows.
 */
export function ringTabs(engagementOn: boolean, crossover = false): (TabItem & { id: TabId })[] {
  const base = crossover ? RING_TABS.filter((t) => t.id !== "gems") : RING_TABS;
  return engagementOn ? [...base, ENGAGEMENT_TAB] : base;
}

interface TabRailProps {
  active: TabId;
  onSelect: (id: TabId) => void;
  className?: string;
}

/**
 * The Ring Builder tab strip. The Engagement tab appears/disappears with the
 * top-bar toggle; the kit `Tabs` underline re-parents to whichever tab is
 * active, so that transition animates instead of popping.
 */
export function TabRail({ active, onSelect, className }: TabRailProps) {
  const { engagementOn, state } = useRing();
  const caps = useCaps();
  const tabs = ringTabs(engagementOn && caps.ringBuilderV37, state.bandStyle === "crossover");

  // Toggling Engagement off — or switching Construction to Cross-over, which
  // drops Gems — while that tab is open would strand the body on a tab that no
  // longer exists; fall back to the first tab. In a multi-ring set the rail is
  // computed from the ACTIVE ring, so this also fires when the user switches
  // from a cross-over ring to a standard one while Gems is showing.
  const stranded = !tabs.some((t) => t.id === active);
  useEffect(() => {
    if (stranded) onSelect(RING_TABS[0].id);
  }, [stranded, onSelect]);

  return (
    <Tabs
      tabs={tabs}
      active={active}
      onSelect={(id) => onSelect(id as TabId)}
      layoutId="ring-tab-active"
      className={className}
    />
  );
}
